// Analytics utility for tracking events with both PostHog and Google Analytics

declare global {
  interface Window {
    // eslint-disable-next-line no-unused-vars
    gtag: (command: string, targetId: string, config?: any) => void
    posthog?: any
  }
}

export interface PurchaseEventData {
  plan_id: string
  plan_name: string
  user_id: string
  subscription_status: string
  purchase_type: 'subscription' | 'one-time'
  value?: number // Purchase amount in dollars
  currency?: string // Currency code (e.g., 'USD')
}

// Check if we're in development mode
const isDevelopment =
  import.meta.env.DEV ||
  window.location.hostname === 'localhost' ||
  window.location.hostname === '127.0.0.1'

/**
 * Track a purchase completion event with both PostHog and Google Analytics
 */
export const trackPurchaseCompleted = (data: PurchaseEventData, posthog?: any) => {
  // Skip tracking in development
  if (isDevelopment) {
    console.log('Analytics disabled in development. Purchase event:', data)
    return
  }

  // Convert integer value to decimal format (e.g., 89 to 89.00)
  const decimalValue = data.value ? parseFloat(data.value.toFixed(2)) : undefined

  // PostHog tracking
  if (posthog) {
    posthog.capture('purchase_completed', {
      ...data,
      value: decimalValue,
      $set: {
        plan_id: data.plan_id,
        plan_name: data.plan_name,
        subscription_status: data.subscription_status,
      },
    })
  }

  // Google Analytics tracking
  if (window.gtag) {
    window.gtag('event', 'purchase', {
      transaction_id: `${data.user_id}_${Date.now()}`, // Generate unique transaction ID
      value: decimalValue,
      currency: data.currency || 'USD',
      items: [
        {
          item_id: data.plan_id,
          item_name: data.plan_name,
          price: decimalValue,
          quantity: 1,
        },
      ],
      custom_parameters: {
        user_id: data.user_id,
        subscription_status: data.subscription_status,
        purchase_type: data.purchase_type,
      },
    })
  }
}

/**
 * Track a pageview event with both PostHog and Google Analytics
 */
export const trackPageView = (path: string, title?: string) => {
  // Skip tracking in development
  if (isDevelopment) {
    console.log('Analytics disabled in development. Pageview:', { path, title })
    return
  }

  // PostHog tracking (handled by usePageTracking hook)

  // Google Analytics tracking
  if (window.gtag) {
    window.gtag('config', 'G-XYBT79P1ZJ', {
      page_path: path,
      page_title: title,
    })
  }
}
